// User roles
export const USER_ROLES = {
  CLIENT: "CLIENT",
  DATA_COLLECTOR: "DATA_COLLECTOR",
  SUPERVISOR: "SUPERVISOR",
  INSTITUTIONAL_CLIENT: "INSTITUTIONAL_CLIENT",
  SYSTEM:  "SYSTEM",
  ADMIN: "ADMIN",
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// User interfaces
export interface BaseUser {
  id: string;
  name: string;
  email: string;
  password: string;
  role: UserRole;
}

export interface ClientUser extends BaseUser {
  role: typeof USER_ROLES.CLIENT;
  governmentId: string;
  businessName: string;
}

export interface FieldCollectorUser extends BaseUser {
  role: typeof USER_ROLES.DATA_COLLECTOR;
}

export interface SupervisorUser extends BaseUser {
  role: typeof USER_ROLES.SUPERVISOR;
  signature: string;
}

export interface AdminUser extends BaseUser {
  role: typeof USER_ROLES.ADMIN;
  signature: string;
}

export type User = ClientUser | FieldCollectorUser | SupervisorUser | AdminUser;

// Default users for testing
export const defaultUsers: User[] = [
  {
    id: "1",
    name: "John Client",
    email: "client@example.com",
    password: "client123",
    role: USER_ROLES.CLIENT,
    governmentId: "GID12345",
    businessName: "ABC Properties",
  },
  {
    id: "2",
    name: "Sarah Field",
    email: "field@example.com",
    password: "field123",
    role: USER_ROLES.DATA_COLLECTOR,
  },
  {
    id: "3",
    name: "Mike Supervisor",
    email: "supervisor@example.com",
    password: "super123",
    role: USER_ROLES.SUPERVISOR,
    signature: "M. Supervisor",
  },
];

// Property types
export const propertyTypes = [
  "Residential",
  "Commercial",
  "Industrial",
  "Agricultural",
  "Mixed Use",
] as const;

export const roofTypes = [
  "Shingle",
  "Metal",
  "Tile",
  "Flat",
  "Green",
  "Thatch",
  "Concrete",
] as const;

export const wallTypes = [
  "Brick",
  "Wood",
  "Stone",
  "Concrete",
  "Stucco",
  "Vinyl",
  "Metal",
] as const;

export const floorTypes = [
  "Hardwood",
  "Tile",
  "Carpet",
  "Laminate",
  "Vinyl",
  "Stone",
  "Concrete",
] as const;

export type PropertyType = (typeof propertyTypes)[number];
export type RoofType = (typeof roofTypes)[number];
export type WallType = (typeof wallTypes)[number];
export type FloorType = (typeof floorTypes)[number];

// Property interfaces
export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface Location {
  coordinates: Coordinates;
  province: string;
  district: string;
  sector: string;
  cell: string;
  village: string;
}

export interface LandParcel {
  area: number; // square meters
  ratePerSqMeter: number; // currency
  tenure: string;
  zoning: string;
}

export interface Roof {
  type: RoofType;
  condition: string;
  area: number; // square meters
}

export interface Walls {
  type: WallType;
  condition: string;
  paintQuality: string;
}

export interface Windows {
  count: number;
  type: string;
  condition: string;
}

export interface Doors {
  count: number;
  type: string;
  condition: string;
}

export interface Floors {
  type: FloorType;
  condition: string;
}

export interface MainBuilding {
  constructedArea: number; // square meters
  yearOfConstruction: number;
  effectiveAge: number; // years
  roof: Roof;
  walls: Walls;
  windows: Windows;
  doors: Doors;
  floors: Floors;
}

export interface Amenities {
  annexBuilding: boolean;
  outsideKitchen: boolean;
  garden: boolean;
  pavedParking: boolean;
  fence: boolean;
  retainingWalls: boolean;
  waterTank: boolean;
  veranda: boolean;
}

export interface Image {
  url: string;
  type: string;
}

export interface ValuationDetails {
  annualDepreciation: number; // percentage
  unitPrice: number; // currency per square meter
  originalConstructionCost: number; // currency
  maintenanceCoefficient: number;
  depreciatedValue: number; // currency
  totalLandValue: number; // currency
  totalPropertyValue: number; // currency
  replacementCost: number; // currency
  insuranceValue: number; // currency
  createdAt: string;
  updatedAt: string;
  status: string;
  certifiedBy: string;
}

export interface Property {
  id: string;
  upi: string;
  ownerIdNumber: string;
  ownerName: string;
  location: Location;
  landParcel: LandParcel;
  mainBuilding: MainBuilding;
  totalValue?: number;
  amenities: Amenities;
  valuationDetails: ValuationDetails;
  images: Image[];
  status?: string;
  createdAt?: string;
}

// Mock properties for testing
export const mockProperties: Property[] = [
  {
    id: "PROP001",
    upi: "UPI12345678",
    ownerIdNumber: "GID12345",
    ownerName: "John Client",
    location: {
      coordinates: {
        latitude: -1.9441,
        longitude: 30.0619,
      },
      province: "Kigali",
      district: "Nyarugenge",
      sector: "Nyamirambo",
      cell: "Rwezamenyo",
      village: "Kabeza",
    },
    landParcel: {
      area: 500, // square meters
      ratePerSqMeter: 150, // currency
      tenure: "Freehold",
      zoning: "Residential",
    },
    mainBuilding: {
      constructedArea: 320, // square meters
      yearOfConstruction: 2010,
      effectiveAge: 10, // years
      roof: {
        type: "Tile",
        condition: "Good",
        area: 350, // square meters
      },
      walls: {
        type: "Brick",
        condition: "Good",
        paintQuality: "Good",
      },
      windows: {
        count: 12,
        type: "Aluminum framed",
        condition: "Good",
      },
      doors: {
        count: 8,
        type: "Wooden",
        condition: "Fair",
      },
      floors: {
        type: "Tile",
        condition: "Good",
      },
    },
    amenities: {
      annexBuilding: true,
      outsideKitchen: false,
      garden: true,
      pavedParking: true,
      fence: true,
      retainingWalls: false,
      waterTank: true,
      veranda: true,
    },
    valuationDetails: {
      annualDepreciation: 2, // percentage
      unitPrice: 400, // currency per square meter
      originalConstructionCost: 128000, // currency
      maintenanceCoefficient: 0.85,
      depreciatedValue: 109824, // currency
      totalLandValue: 75000, // currency
      totalPropertyValue: 184824, // currency
      replacementCost: 115200, // currency
      insuranceValue: 115200, // currency
      createdAt: "2023-06-15",
      updatedAt: "2023-06-15",
      status: "Completed",
      certifiedBy: "Mike Supervisor",
    },
    images: [
      {
        url: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914",
        type: "exterior_front",
      },
      {
        url: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c",
        type: "exterior_back",
      },
    ],
  },
  {
    id: "PROP002",
    upi: "UPI87654321",
    ownerIdNumber: "GID67890",
    ownerName: "Jane Owner",
    location: {
      coordinates: {
        latitude: -1.956,
        longitude: 30.082,
      },
      province: "Kigali",
      district: "Kicukiro",
      sector: "Gikondo",
      cell: "Kagunga",
      village: "Gasharu",
    },
    landParcel: {
      area: 350, // square meters
      ratePerSqMeter: 180, // currency
      tenure: "Leasehold",
      zoning: "Residential",
    },
    mainBuilding: {
      constructedArea: 280, // square meters
      yearOfConstruction: 2015,
      effectiveAge: 5, // years
      roof: {
        type: "Metal",
        condition: "Excellent",
        area: 300, // square meters
      },
      walls: {
        type: "Concrete",
        condition: "Excellent",
        paintQuality: "Excellent",
      },
      windows: {
        count: 10,
        type: "PVC framed",
        condition: "Excellent",
      },
      doors: {
        count: 7,
        type: "Wooden",
        condition: "Good",
      },
      floors: {
        type: "Hardwood",
        condition: "Excellent",
      },
    },
    amenities: {
      annexBuilding: false,
      outsideKitchen: false,
      garden: true,
      pavedParking: true,
      fence: true,
      retainingWalls: true,
      waterTank: true,
      veranda: true,
    },
    valuationDetails: {
      annualDepreciation: 1.5, // percentage
      unitPrice: 450, // currency per square meter
      originalConstructionCost: 126000, // currency
      maintenanceCoefficient: 0.95,
      depreciatedValue: 119700, // currency
      totalLandValue: 63000, // currency
      totalPropertyValue: 182700, // currency
      replacementCost: 126000, // currency
      insuranceValue: 126000, // currency
      createdAt: "2023-07-20",
      updatedAt: "2023-07-20",
      status: "Completed",
      certifiedBy: "Mike Supervisor",
    },
    images: [
      {
        url: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9",
        type: "exterior_front",
      },
      {
        url: "https://images.unsplash.com/photo-1600566752355-35792bedcfea",
        type: "exterior_back",
      },
    ],
  },
  {
    id: "PROP003",
    upi: "UPI56781234",
    ownerIdNumber: "GID24680",
    ownerName: "Robert Property",
    location: {
      coordinates: {
        latitude: -1.938,
        longitude: 30.119,
      },
      province: "Kigali",
      district: "Gasabo",
      sector: "Kimironko",
      cell: "Bibare",
      village: "Gatare",
    },
    landParcel: {
      area: 420, // square meters
      ratePerSqMeter: 200, // currency
      tenure: "Freehold",
      zoning: "Commercial",
    },
    // This property is in progress - data being collected
    status: "In Progress",
    createdAt: "2023-08-05",
  } as unknown as Property, // Partial property, so we need to cast
];

// Calculation functions
export interface PropertyValueCalculation {
  landValue: number;
  depreciatedValue: number;
  replacementCost: number;
  insuranceValue: number;
  totalValue: number;
  error?: string;
}

export const calculatePropertyValue = (
  property: Partial<Property>
): PropertyValueCalculation => {
  // Basic structure - actual implementation would be more complex
  if (!property.mainBuilding || !property.landParcel) {
    return {
      landValue: 0,
      depreciatedValue: 0,
      replacementCost: 0,
      insuranceValue: 0,
      totalValue: 0,
      error: "Incomplete data",
    };
  }

  const landValue =
    property.landParcel.area * property.landParcel.ratePerSqMeter;

  // Calculate depreciation
  const age = property.mainBuilding.effectiveAge;
  const originalCost =
    property.mainBuilding.constructedArea *
    (property.valuationDetails?.unitPrice || 400);
  const annualDepRate = property.valuationDetails?.annualDepreciation || 2;
  const maintCoeff = property.valuationDetails?.maintenanceCoefficient || 0.85;

  const depreciationFactor = 1 - (age * annualDepRate) / 100;
  const depreciatedValue = originalCost * depreciationFactor * maintCoeff;

  // Calculate replacement cost (also used for insurance)
  const replacementCost =
    property.mainBuilding.constructedArea *
    (property.valuationDetails?.unitPrice || 400) *
    maintCoeff;

  // Total property value
  const totalValue = landValue + depreciatedValue;

  return {
    landValue,
    depreciatedValue,
    replacementCost,
    insuranceValue: replacementCost,
    totalValue,
  };
};
